import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { Pencil } from 'lucide-react';
import type { Deliverable } from './CreateProjectContext';

import Format from './DeliverableCardSelect/Format';
import FrameRate from './DeliverableCardSelect/FrameRate';
import Resolution from './DeliverableCardSelect/Resolution';
import Length from './DeliverableCardSelect/Length';

interface EditDeliverableDialogProps {
  deliverable: Deliverable;
  onUpdate: (updated: Partial<Deliverable>) => void | Promise<void>;
}

export default function EditDeliverableDialog({
  deliverable,
  onUpdate,
}: EditDeliverableDialogProps) {
  const [open, setOpen] = useState(false);

  const [videoType, setVideoType] = useState(deliverable.videoType ?? '');
  const [lengthRange, setLengthRange] = useState(deliverable.lengthRange ?? '');
  const [aspectRatio, setAspectRatio] = useState(deliverable.aspectRatio ?? '');
  const [resolution, setResolution] = useState(deliverable.resolution ?? '');
  const [frameRate, setFrameRate] = useState(deliverable.frameRate ?? '');

  async function handleSave() {
    const updated: Partial<Deliverable> = {
      videoType,
      lengthRange,
      aspectRatio,
      resolution,
      frameRate,
    };
    await onUpdate(updated);
    setOpen(false);
  }

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) {
      setVideoType(deliverable.videoType ?? '');
      setLengthRange(deliverable.lengthRange ?? '');
      setAspectRatio(deliverable.aspectRatio ?? '');
      setResolution(deliverable.resolution ?? '');
      setFrameRate(deliverable.frameRate ?? '');
    }
    setOpen(isOpen);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button className="cursor-pointer" variant={'outline'} size={'sm'}>
          <Pencil className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Video</DialogTitle>
          <DialogDescription>
            Make changes to your video specifications.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-2 py-4">
          <label className="text-sm">Length</label>
          <Length
            value={lengthRange}
            onChange={(value) => setLengthRange(value)}
          />

          <label className="text-sm">Format</label>
          <Format
            value={aspectRatio}
            onChange={(value) => setAspectRatio(value)}
          />

          <label className="text-sm">Resolution</label>
          <Resolution
            value={resolution}
            onChange={(value) => setResolution(value)}
          />

          <label className="text-sm">Frame Rate</label>
          <FrameRate
            value={frameRate}
            onChange={(value) => setFrameRate(value)}
          />
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant={'ghost'} className='cursor-pointer'>Cancel</Button>
          </DialogClose>
          <Button onClick={handleSave} className='cursor-pointer'>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
